import { Injectable } from '@angular/core';
import { Payment } from '@repo/models';
import { asyncScheduler, scheduled } from 'rxjs';
import { DatabaseService } from './database.service';

export enum PaymentType {
  TOGETHER = 'TOGETHER',
  PERSON = 'PERSON',
  ITEM = 'ITEM',
}

export enum PaymentMethod {
  CARD = 'CARD',
  CASH = 'CASH',
  BOTH = 'BOTH',
}

const MENU_ITEM_ORDER_MODIFIER_FRAGMENT = `
    modifiers:menu_item_order_modifier (
      quantity,
      option:modifier_option (
        translations:modifier_option_translation (
          name,
          languageCode:language_code
        ),
        modifier:modifier (
          translations:modifier_translation (
            name,
            languageCode:language_code
          )
        )
      )
    )
`;

const PAYMENT_ITEM_QUERY = `
  id,
  paymentId:payment_id,
  quantity,
  netAmount:net_amount,
  grossAmount:gross_amount,
  vatAmount:vat_amount,
  vatRate:vat_rate,
  order:menu_item_orders (
    menuItem:menu_items (
      id,
      internalId:internal_id,
      posName:pos_name,
      posDescription:pos_description,
      translations:menu_item_translations (
        id,
        name,
        description,
        languageCode:language_code
      )
    ),
    ${MENU_ITEM_ORDER_MODIFIER_FRAGMENT}
  )
`;

const MENU_ITEM_ORDER_QUERY = `
  id,
  menuItem:menu_items (
    id,
    internalId:internal_id,
    posName:pos_name,
    translations:menu_item_translations (
      id,
      name,
      description,
      languageCode:language_code
    )
  ),
  ${MENU_ITEM_ORDER_MODIFIER_FRAGMENT}
`;

const DEAL_ORDER_QUERY = `
  id,
  deal:deals (
    id,
    translations:deal_translations (
      id,
      name,
      description,
      languageCode:language_code
    ),
    items:deal_items (
      menuItem:menu_items (
        id,
        internalId:internal_id,
        posName:pos_name,
        translations:menu_item_translations (
          id,
          name,
          description,
          languageCode:language_code
        )
      )
    )
  ),
  modifiers:deal_order_modifier (
    menuItemId:menu_item_id,
    quantity,
    option:modifier_option (
      translations:modifier_option_translation (
        name,
        languageCode:language_code
      ),
      modifier:modifier (
        translations:modifier_translation (
          name,
          languageCode:language_code
        )
      )
    )
  )
`;

@Injectable({
  providedIn: 'root',
})
export class PaymentService extends DatabaseService<Payment, any> {
  public override getRequest(entity: Payment): PaymentRequest {
    throw new Error('Method not implemented.');
  }
  public override createEntity(entity: Payment): Promise<Payment> {
    throw new Error('Method not implemented.');
  }
  public override updateEntity(uuid: string, entity: Payment): Promise<Payment> {
    throw new Error('Method not implemented.');
  }
  public override deleteEntity(uuid: string): Promise<boolean> {
    throw new Error('Method not implemented.');
  }
  constructor() {
    super();
  }

  public override async getEntities(): Promise<Payment[]> {
    return new Promise<Payment[]>(async (resolve) => {
      const client = this.getDatabaseClient();
      const { data, error } = await client
        .from('payments')
        .select(
          `
              id,
              type,
              method,
              createdAt:created_at,
              updatedAt:updated_at
        `
        )
        .order('created_at', { ascending: false });

      if (error != null) {
        console.error(error);
        resolve([]);
      }

      resolve(data as unknown as Payment[]);
    });
  }

  public getPaymentItems(paymentIds: string[]) {
    return this.selectByIds('payment_items', PAYMENT_ITEM_QUERY, 'payment_id', paymentIds);
  }

  public getMenuItemOrders(menuItemOrderIds: string[]) {
    return this.selectByIds('menu_item_orders', MENU_ITEM_ORDER_QUERY, 'id', menuItemOrderIds);
  }

  public getDealOrders(dealOrderIds: string[]) {
    return this.selectByIds('deal_orders', DEAL_ORDER_QUERY, 'id', dealOrderIds);
  }

  private selectByIds(table: string, queryText: string, idColumn: string, ids: string[]) {
    const client = this.getDatabaseClient();
    const query = client.from(table).select(queryText).in(idColumn, ids);
    return scheduled(query, asyncScheduler);
  }
}
